import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Logo from './Logo';
import InvestorTimeline from './InvestorTimelineComponent';
import { Button } from '../ui';
import api from '@/config/axios';
import {
  LayoutDashboard,
  Wallet,
  Users,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Moon,
  Sun,
  Building2,
  DollarSign,
  Receipt,
  TrendingUp as TrendingUpIcon,
  AlertCircle,
  History,
  BarChart3,
  PieChart,
} from 'lucide-react';
import NotificationBell from './NotificationBell';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [brokerName, setBrokerName] = useState(null);
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Fetch broker information for investors
  useEffect(() => {
    const fetchBrokerInfo = async () => {
      if (user?.role === 'investor' && user?.id) {
        try {
          const response = await api.get('/api/broker-investors');
          const brokerInvestors = response.data.brokerInvestors || [];
          // Since API now filters by investor_id, we should get only this investor's relationship
          const relationship = brokerInvestors[0];
          if (relationship?.broker_id?.name) {
            setBrokerName(relationship.broker_id.name);
          }
        } catch (error) {
          console.error('Error fetching broker info:', error);
          // Silently fail - broker name is optional
        }
      }
    };

    if (user?.role === 'investor') {
      fetchBrokerInfo();
    } else {
      setBrokerName(null);
    }
  }, [user]);

  const navigation = [
    ...(user?.role === 'broker' ? [
      { name: 'Dashboard', href: '/', icon: LayoutDashboard },
      { name: 'Broker Investors', href: '/broker-investors', icon: Users },
    ] : user?.role === 'investor' ? [
      { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    ] : [
      { name: 'Dashboard', href: '/', icon: LayoutDashboard },
      { name: 'Portfolio', href: '/portfolio', icon: Wallet },
      ...(user?.role === 'admin' ? [
        { name: 'Investors', href: '/investors', icon: Users },
        { name: 'Brokers', href: '/brokers', icon: Building2 },
        { name: 'Payouts', href: '/payouts', icon: DollarSign },
        { name: 'Expenses', href: '/expenses', icon: Receipt },
        { name: 'Admin Profit', href: '/admin-profit', icon: TrendingUpIcon },
        { name: 'ROI Debugger', href: '/admin-roi-debug', icon: BarChart3 },
        { name: 'Profit Allocation', href: '/profit-allocation', icon: PieChart },
        { name: 'Payout Alerts', href: '/payout-alerts', icon: AlertCircle },
        { name: 'Transaction History', href: '/transaction-history', icon: History },
        { name: 'Users', href: '/users', icon: Users },
        { name: 'Audit Logs', href: '/logs', icon: FileText },
      ] : []),
      { name: 'Settings', href: '/settings', icon: Settings },
    ]),
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="h-screen overflow-hidden bg-accent-50 dark:bg-black relative">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Grounded, not floating */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-accent-50 dark:bg-black border-r border-accent-200 dark:border-primary-800 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-primary-200 dark:border-primary-700">
            <Link to="/" className="flex items-center">
              <Logo className="h-10 w-auto" />
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-primary-500 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
            >
              <X size={24} />
            </button>
          </div>


          {/* Navigation */}
          <nav className={`px-3 py-4 space-y-1 overflow-y-auto ${user?.role === 'investor' ? 'flex-shrink-0' : 'flex-1'}`}>
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200
                    ${active
                      ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-200'
                      : 'text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/50'
                    }
                  `}
                >
                  <Icon size={20} className="flex-shrink-0" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* Investor Timeline */}
          {user?.role === 'investor' && (
            <div className="flex-1 overflow-y-auto min-h-0 pt-2 pb-4">
              <InvestorTimeline />
            </div>
          )}

          {/* User section */}
          <div className="p-4 border-t border-primary-200 dark:border-primary-700">
            <div className="flex items-center gap-3 mb-4 px-2">
              <div className="w-12 h-12 rounded-full bg-primary-600 dark:bg-primary-700 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-primary-900 dark:text-primary-50 truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-primary-500 dark:text-primary-400 capitalize">
                  {user?.role}
                </p>
                {user?.role === 'investor' && brokerName && (
                  <p className="text-xs text-primary-600 dark:text-primary-400 mt-1 flex items-center gap-1">
                    <Building2 size={12} />
                    <span className="truncate">Broker: {brokerName}</span>
                  </p>
                )}
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start"
              leftIcon={<LogOut size={18} />}
            >
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-40 bg-white dark:bg-primary-800 border-b border-primary-200 dark:border-primary-700 shadow-[0_1px_3px_0_rgba(0,0,0,0.05),0_1px_2px_-1px_rgba(0,0,0,0.05)] dark:shadow-[0_1px_3px_0_rgba(0,0,0,0.3),0_1px_2px_-1px_rgba(0,0,0,0.2)] backdrop-blur-sm bg-white/95 dark:bg-primary-800/95">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-primary-500 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
            >
              <Menu size={24} />
            </button>

            <div className="flex items-center gap-3 ml-auto">
              <NotificationBell user={user} />
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-primary-500 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900 transition-colors"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8 relative z-10 max-h-[calc(100vh-4rem)] overflow-auto">{children}</main>
      </div>
    </div>
  );
};

export default Layout;

